/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useRouteError } from 'react-router-dom';
import { Typography, Box, Container } from '@mui/material';
import { PageLayout } from '@/components/layouts/PageLayout';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import {
	getSafeUserErrorMessage,
	logApplicationError,
	parseRouteErrorData,
} from '@/utils/safeErrorMessage';

export const ErrorPage = () => {
	const error = useRouteError();
	const location = useLocation();
	const params = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const data = useMemo(() => parseRouteErrorData(error), [error]);

	useEffect(() => {
		logApplicationError('Route error', {
			message: error?.message ?? error?.statusText,
			stack: error?.stack,
			status: error?.status,
			data: error?.data,
			pathname: location.pathname,
			parsedData: data,
			userAgent: navigator.userAgent,
		});
	}, [error, location.pathname, data]);

	useEffect(() => {
		if (data?.status === 'rejected') {
			const id = params?.dealId ?? params?.clientId ?? '';
			const path = location.pathname.replace(id, '');

			const message =
				data?.reason !== 'USER_FORBIDDEN'
					? `Error: ${
							params?.dealId ? `deal ${params.dealId}` : `client ${params.clientId}`
						} does not exist. It may have been deleted or you may not have the correct permissions.`
					: `Error: you do not have the correct permissions to access ${
							params?.dealId ? `deal ${params.dealId}` : `client ${params.clientId}`
						}.`;
			const payloadId = `${params?.dealId ?? params?.clientId}-invalid`;

			if (params?.dealId) {
				dispatch(
					showToast({
						message,
						id: payloadId,
						error: true,
						duration: 5000,
						autohide: true,
					}),
				);
				navigate(path);
			} else if (params?.clientId) {
				dispatch(
					showToast({
						message,
						id: payloadId,
						error: true,
						duration: 5000,
						autohide: true,
					}),
				);
				navigate(path);
			}
		}
	}, [
		data?.status,
		data?.reason,
		dispatch,
		location.pathname,
		navigate,
		params?.clientId,
		params?.dealId,
	]);

	const userMessage = getSafeUserErrorMessage(
		error,
		'Sorry, an unexpected error has occurred. Please try again or contact support if the problem persists.',
	);

	return (
		<PageLayout>
			<Container>
				<Box sx={{ paddingTop: '4em' }}>
					<Typography variant='h4'>{userMessage}</Typography>
				</Box>
			</Container>
		</PageLayout>
	);
};
